import { useEffect, useState } from "react";
import {
  Modal,
  Paper,
  Grid,
  Button,
  Typography,
  Tabs,
  Tab,
  Box,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Table,
  TableBody,
} from "@mui/material";
import { ClientPreference, SettlementType } from "types";
import { RxDownload, RxUpload } from "react-icons/rx";
import { BsBoxFill } from "react-icons/bs";
import { settlementTypeName } from "helpers/settlements";
import CustomNumberInput from "components/CustomNumberInput";
import useCrud from "hooks/useCrud";
import { SETTLEMENT_TYPES_URL } from "constants/urls";

type Props = {
  open: boolean;
  handleOpen: () => void;
  onEdit: (preferences: ClientPreference[]) => void;
};

const ClientSettlementTypesTableHeader = ({
  columns,
}: {
  columns: string[];
}) => {
  return (
    <TableHead>
      <TableRow>
        {columns.map((column) => {
          return (
            <TableCell align={"left"}>
              <Typography variant="body2">{column}</Typography>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
};

const ClientSettlementTypesTableRow = ({
  content,
  handlePriceChange,
}: {
  content: ClientPreference;
  handlePriceChange: (
    type: string | undefined,
    movement_type: string,
    price: number,
  ) => void;
}) => {
  return (
    <TableRow>
      <TableCell>
        <Typography variant="body1" gutterBottom>
          {settlementTypeName(content.settlement_type.description)}
        </Typography>
      </TableCell>
      <TableCell>
        <CustomNumberInput
          label="%"
          value={content.price}
          onChange={(e) => {
            const value = parseInt(e.target.value);
            handlePriceChange(
              content.settlement_type.description,
              content.movement_type,
              value,
            );
          }}
        />
      </TableCell>
    </TableRow>
  );
};

const ClientSettlementTypesTable = ({
  clientPreferences,
  handlePriceChange,
}: {
  clientPreferences: ClientPreference[];
  handlePriceChange: (
    type: string | undefined,
    movement_type: string,
    price: number,
  ) => void;
}) => {
  return (
    <TableContainer component={Paper}>
      <Table size="small">
        <ClientSettlementTypesTableHeader
          columns={["Tipo de liquidación", "Precio"]}
        />
        <TableBody>
          {clientPreferences.map((row, index) => {
            return (
              <ClientSettlementTypesTableRow
                key={index}
                content={row}
                handlePriceChange={handlePriceChange}
              />
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default function EditMassiveClientModal({
  open = true,
  handleOpen,
  onEdit,
}: Props) {
  const MOVEMENT_TYPES = ["INGRESO", "ESTADIA", "EGRESO"];
  const [currentTab, setCurrentTab] = useState<number>(1);

  const [clientSettlementTypes, setClientSettlementTypes] = useState<
    ClientPreference[]
  >([]);

  const { data: settlementTypes } =
    useCrud<SettlementType>(SETTLEMENT_TYPES_URL);

  useEffect(() => {
    if (open) {
      settlementTypes.forEach((settlementType) => {
        MOVEMENT_TYPES.forEach((movementType) => {
          setClientSettlementTypes((prevState) => [
            ...prevState,
            {
              settlement_type_id: settlementType.id,
              movement_type: movementType,
              price: 0,
              is_default: false,
              settlement_type: settlementType,
            },
          ]);
        });
      });
    } else {
      setClientSettlementTypes([]);
    }
  }, [open]);

  const handlePriceChange = (
    type: string | undefined,
    movement_type: string,
    price: number,
  ) => {
    let update = clientSettlementTypes.map((clientSettlementTypes) => {
      if (
        clientSettlementTypes.settlement_type?.description === type &&
        clientSettlementTypes.movement_type === movement_type
      ) {
        clientSettlementTypes.price = Number(price);
      }
      return clientSettlementTypes;
    });
    setClientSettlementTypes(update);
  };

  const onSubmit = (preferences: ClientPreference[]) => {
    onEdit(preferences);
    handleOpen();
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Modal open={open} onClose={handleOpen}>
      <Paper
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { sm: "90%", md: "30rem" },
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Grid container spacing={2}>
          <Typography variant="h5" gutterBottom>
            Editar precio de tipos de liquidación
          </Typography>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              centered
              textColor="primary"
            >
              <Tab
                sx={{ textTransform: "none" }}
                value={1}
                label={
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.5}
                  >
                    <RxDownload />
                    <Typography variant="body1">Ingreso *</Typography>
                  </Box>
                }
              />
              <Tab
                sx={{ textTransform: "none" }}
                value={2}
                label={
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.5}
                  >
                    <BsBoxFill />
                    <Typography variant="body1">Estadía *</Typography>
                  </Box>
                }
              />
              <Tab
                sx={{ textTransform: "none" }}
                value={3}
                label={
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    gap={0.5}
                  >
                    <RxUpload />
                    <Typography variant="body1">Egreso *</Typography>
                  </Box>
                }
              />
            </Tabs>
          </Grid>
          <Grid item xs={12} p={2}>
            <ClientSettlementTypesTable
              handlePriceChange={handlePriceChange}
              clientPreferences={clientSettlementTypes.filter(
                (clientSettlementType) =>
                  clientSettlementType.movement_type ===
                  MOVEMENT_TYPES[currentTab - 1],
              )}
            />
          </Grid>
          <Grid item xs={12} display="flex" justifyContent="end" gap={1}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                handleOpen();
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              onClick={() => {
                onSubmit(clientSettlementTypes);
              }}
            >
              Guardar
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Modal>
  );
}
